import { Injectable, InjectionToken, Provider } from '@angular/core';
import { mockCategoryDiagnose } from '../../mock-data/category-diagnose.mock';
import { mockDisabilityType } from '../../mock-data/disability-type.mock';
import { mockDocument } from '../../mock-data/document-file.mock';
import { mockUser } from '../../mock-data/user.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { buildPaginationQuery } from '../../shared/pagination/pagination.builder';
import { ApiDisabilityKind, ApiDisabilityKindUpdateBody, ApiDisabilityType } from '../api-types/disability-type.api';
import { CreateDisabilityKind, DisabilityKind, DisabilityType } from '../app-types/disability-type.app';
import {
	transformApiDisabilityKindNeighborsToAppDisabilityKindNeighbors,
	transformApiDisabilityKindsToAppDisabilityKinds,
	transformApiDisabilityKindToAppDisabilityKind,
	transformApiDisabilityTypesToAppDisabilityTypes,
} from '../transformers/disability-type.transform';
import { BaseRequestService } from './base-request.service';
import { ApiResponse, Metadata } from '../api-types/api-response.api';
import { NeighborContext } from '../app-types/neighbors.app';
import { ApiNeighborContext } from '../api-types/neighbor.api';

export const DISABILITY_TYPE_SERVICE = new InjectionToken<DisabilityTypeService>('DISABILITY_TYPE_SERVICE');

@Injectable()
export class DisabilityTypeService extends BaseRequestService {
	public getAll = async (offset: number, range: number) => {
		const response = await this.get<PaginatedResponse<ApiDisabilityType>>(
			'categories/disabilityType',
			buildPaginationQuery(offset, range),
		);

		const result: PaginatedResponse<DisabilityType> = {
			entities: transformApiDisabilityTypesToAppDisabilityTypes(response.entities),
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return result;
	};

	public getAllKinds = async (clientId: number, offset: number, range: number) => {
		const response = await this.get<PaginatedResponse<ApiDisabilityKind>>(
			`disabilityKinds/${clientId}`,
			buildPaginationQuery(offset, range),
		);

		const result: PaginatedResponse<DisabilityKind> = {
			entities: transformApiDisabilityKindsToAppDisabilityKinds(response.entities),
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return result;
	};

	public getSingleKind = async (clientId: number, kindId: number) => {
		const response = await this.get<ApiResponse<ApiDisabilityKind, Metadata>>(`disabilityKinds/${clientId}/${kindId}`);
		const result: DisabilityKind = transformApiDisabilityKindToAppDisabilityKind(response.data);
		return { data: result, metadata: response.metadata };
	};

	public create = async (clientId: number, data: CreateDisabilityKind) => {
		const apiResponse = await this.post<ApiDisabilityKind, CreateDisabilityKind>(`disabilityKinds/${clientId}`, data);
		const kind = transformApiDisabilityKindToAppDisabilityKind(apiResponse);
		return kind;
	};

	public update = async (clientId: number, body: ApiDisabilityKindUpdateBody) => {
		const response = await this.put<ApiDisabilityKindUpdateBody, ApiDisabilityKindUpdateBody>(
			`disabilityKinds/${clientId}`,
			body,
		);
		return response;
	};

	public delete = async (clientId: number, reportId: number) => {
		await this.remove<void>(`disabilityKinds/${clientId}/${reportId}`);
	};

	public async getKindNeighbors(
		clientId: number | string,
		kindId: number | string,
	): Promise<NeighborContext<DisabilityKind>> {
		const response = await this.get<ApiNeighborContext<ApiDisabilityKind>>(
			`disabilityKinds/${clientId}/${kindId}/neighbors`,
		);
		return transformApiDisabilityKindNeighborsToAppDisabilityKindNeighbors(response);
	}
}

@Injectable()
export class DisabilityTypeMockService extends BaseRequestService {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Disability Type Mock Service: You are currently using a mock service!');
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getAll = async (clientId: number, offset: number, range: number) => {
		const disabilityTypes: DisabilityKind[] = [
			{
				id: 1,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 2,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 3,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 4,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 5,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 6,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				categoryDiagnosis: mockCategoryDiagnose,
				clientId: 1,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
		];

		const pageResult: PaginatedResponse<DisabilityKind> = {
			entities: disabilityTypes,
			offset: 0,
			range: 100,
			totalEntities: disabilityTypes.length,
		};

		return Promise.resolve(pageResult);
	};

	public getSingleKind = async (clientId: number, disabilityTypeId: number) => {
		const disabilityTypes: DisabilityKind[] = [
			{
				id: 1,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 2,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 3,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 4,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 5,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
			{
				id: 6,
				advisor: mockUser,
				disabilityType: mockDisabilityType,
				clientId: 1,
				categoryDiagnosis: mockCategoryDiagnose,
				documents: [mockDocument],
				statusAsOf: new Date(),
			},
		];

		return Promise.resolve(disabilityTypes.find((disability) => disability.id == disabilityTypeId));
	};

	public create = async (clientId: number, data: CreateDisabilityKind) => {
		console.warn('[Mock] Disability Type was created:', data);
		const response = await Promise.resolve({ id: 1 });
		return response.id;
	};
}

export const provideDisabilityTypeApi: Provider = {
	provide: DISABILITY_TYPE_SERVICE,
	useClass: DisabilityTypeService,
};
